import { useCallback, useEffect, useState } from "react";
import HttpService from "../../../hooks/Https-services";
import { APIRoutes } from "../../../utils/constants";

/**
 * The tenant's own game summary, for the Audience Layer's Story tab.
 *
 * PRE-game while the game is live, POST-game once it is not. That reads
 * backwards at a glance and is not: a live game's story is its preview, and
 * only a finished game has a report. It is the same rule the legacy sidebar's
 * WriteUpComponent has always applied, kept identical so a fan reads the same
 * words wherever they are.
 *
 * `enabled` exists so exactly one fetch happens per page. The layer and the
 * legacy sidebar are mutually exclusive (see WatchSidebar), and the sidebar
 * fetches this for itself; without the gate, a tenant with the layer switched
 * off would ask for the same summary twice.
 *
 * A failure is a null summary, not an error state. The Story tab renders its
 * own empty line for that, and a game story nobody wrote is the same thing to
 * a fan as one that could not be loaded.
 */
/**
 * Which of the two stories a given page is showing.
 *
 * The same rule useGameSummary fetches by, named so the layer's Story heading
 * cannot drift away from the words under it: the tab has to call a preview a
 * preview, and the only thing that knows which one it fetched is this file.
 */
export function gameSummaryKind(isLive: boolean): "preview" | "summary" {
  return isLive ? "preview" : "summary";
}

export function useGameSummary(args: {
  gameId: string | undefined;
  isLive: boolean;
  enabled: boolean;
}): string | null {
  const { gameId, isLive, enabled } = args;
  const [summary, setSummary] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!enabled || !gameId) {
      setSummary(null);
      return;
    }
    try {
      const res = await HttpService.get(
        APIRoutes.GetGameSummaryByGameId.replace(":gameId", gameId),
      );
      const data = res?.data?.data;
      const text = isLive ? data?.PreGameSummary : data?.PostGameSummary;
      setSummary(typeof text === "string" && text.trim() !== "" ? text : null);
    } catch {
      // Logged nowhere on purpose: the sidebar's own copy of this already
      // console.errors it, and a missing story is not a failure worth two
      // lines in a fan's console.
      setSummary(null);
    }
  }, [enabled, gameId, isLive]);

  useEffect(() => { void load(); }, [load]);

  return summary;
}
